/**
 * Adaptadores del NAVEGADOR para la cascada del lector.
 *
 * Acá vive todo lo que depende del entorno —canvas, tesseract, el decodificador
 * de códigos— y nada de la secuencia. En node hay otro adaptador con sharp y la
 * secuencia es la misma: eso es lo que permite probar la librería con fotos
 * reales fuera del navegador.
 *
 * Ninguna operación modifica el lienzo que recibe: todas devuelven uno nuevo.
 * El original de la persona queda tal cual entró.
 */
import { AdaptadorDeCodigo, AdaptadorDeImagen, AdaptadorDeOcr, Adaptadores, ImagenConMedidas } from "./pipeline";
import { Rectangulo } from "./types";
import { OcrEngine } from "../tesseractLoader";
import { DecodificadorDeCodigos } from "../zxingLoader";

export interface LienzoCanvas extends ImagenConMedidas {
  canvas: HTMLCanvasElement;
}

export function lienzoDe(canvas: HTMLCanvasElement): LienzoCanvas {
  return { canvas, ancho: canvas.width, alto: canvas.height };
}

function nuevo(ancho: number, alto: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(ancho));
  canvas.height = Math.max(1, Math.round(alto));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("image_error");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  return { canvas, ctx };
}

export const adaptadorDeImagenCanvas: AdaptadorDeImagen<LienzoCanvas> = {
  async girar(imagen, grados) {
    const g = ((grados % 360) + 360) % 360;
    if (g === 0) return imagen;
    const cuartoImpar = g === 90 || g === 270;
    const { canvas, ctx } = nuevo(
      cuartoImpar ? imagen.alto : imagen.ancho,
      cuartoImpar ? imagen.ancho : imagen.alto
    );
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((g * Math.PI) / 180);
    ctx.drawImage(imagen.canvas, -imagen.ancho / 2, -imagen.alto / 2);
    return lienzoDe(canvas);
  },

  async recortar(imagen, caja: Rectangulo) {
    const x = Math.max(0, Math.round(caja.x));
    const y = Math.max(0, Math.round(caja.y));
    const w = Math.min(imagen.ancho - x, Math.round(caja.w));
    const h = Math.min(imagen.alto - y, Math.round(caja.h));
    const { canvas, ctx } = nuevo(w, h);
    ctx.drawImage(imagen.canvas, x, y, w, h, 0, 0, w, h);
    return lienzoDe(canvas);
  },

  async escalarAAncho(imagen, ancho) {
    const factor = ancho / imagen.ancho;
    const { canvas, ctx } = nuevo(ancho, imagen.alto * factor);
    ctx.drawImage(imagen.canvas, 0, 0, canvas.width, canvas.height);
    return lienzoDe(canvas);
  },

  async pixeles(imagen) {
    // Reducida: el contorno trabaja a 360 px y una foto entera son diez megas.
    const factor = Math.max(1, Math.max(imagen.ancho, imagen.alto) / 720);
    const { canvas, ctx } = nuevo(imagen.ancho / factor, imagen.alto / factor);
    ctx.drawImage(imagen.canvas, 0, 0, canvas.width, canvas.height);
    const d = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return { img: { data: d.data, width: d.width, height: d.height }, factor: imagen.ancho / canvas.width };
  },

  async aBlob(imagen) {
    return new Promise<Blob>((resolve, reject) => {
      imagen.canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("image_error"))),
        "image/png"
      );
    });
  },
};

/** Copia en grises. El OCR no usa el color y el ruido cromático lo estorba. */
function aGrises(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const { canvas: salida, ctx } = nuevo(canvas.width, canvas.height);
  ctx.drawImage(canvas, 0, 0);
  const d = ctx.getImageData(0, 0, salida.width, salida.height);
  const px = d.data;
  for (let i = 0; i < px.length; i += 4) {
    const g = (px[i] * 299 + px[i + 1] * 587 + px[i + 2] * 114) / 1000;
    px[i] = px[i + 1] = px[i + 2] = g;
  }
  ctx.putImageData(d, 0, 0);
  return salida;
}

export function adaptadorDeOcr(
  motor: OcrEngine,
  motorMrz?: OcrEngine
): AdaptadorDeOcr<LienzoCanvas> {
  return {
    leerMrz: motorMrz
      ? async (imagen) => {
          const r = await motorMrz.recognize(aGrises(imagen.canvas), {
            whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<",
            psm: "6",
          });
          return { texto: r.text, palabras: r.words, simbolos: r.symbols };
        }
      : undefined,
    async leer(imagen, whitelist) {
      // A grises, igual que el adaptador de node. Si los dos lados no hacen lo
      // mismo, lo que se prueba desde la línea de comandos no es lo que corre
      // en el navegador — y de hecho daban resultados distintos.
      const r = await motor.recognize(
        aGrises(imagen.canvas),
        whitelist ? { whitelist, psm: "6" } : undefined
      );
      return { texto: r.text, palabras: r.words, simbolos: r.symbols };
    },
  };
}

export function adaptadorDeCodigo(decodificador: DecodificadorDeCodigos): AdaptadorDeCodigo {
  return {
    async leer(blob) {
      const encontrados = await decodificador.leer(blob);
      return encontrados.map((c) => ({
        texto: c.texto,
        formato: c.formato,
        esquinas: c.esquinas as never,
      }));
    },
  };
}

/** Los tres adaptadores listos, con el decodificador opcional. */
export function adaptadoresDelNavegador(
  motor: OcrEngine,
  decodificador?: DecodificadorDeCodigos,
  motorMrz?: OcrEngine
): Adaptadores<LienzoCanvas> {
  return {
    imagen: adaptadorDeImagenCanvas,
    ocr: adaptadorDeOcr(motor, motorMrz),
    codigo: decodificador ? adaptadorDeCodigo(decodificador) : undefined,
  };
}
